import { useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import {
  FolderOutlined,
  GraphicEq,
  ExtensionOutlined,
  Launch,
} from '@mui/icons-material';
import { Mono, useModeTokens } from '@/common/components/ui';
import { Waveform } from '@/common/components/signal';
import { heroWave } from '@/routes/signal/fixtures';
import { LandingSpotlight } from './LandingEffects';
import { Headline, Eyebrow } from './primitives';
import { ANCHOR } from './content';

const VIEWS = [
  {
    label: 'Project library',
    icon: FolderOutlined,
    title: 'Find the session. Get back into it.',
    body: 'Search your projects, switch between grid and list views, and open a linked local session in its DAW with the desktop app connected.',
    note: 'Your existing folders. Your installed DAW. One place to find the work.',
  },
  {
    label: 'Files & stems',
    icon: GraphicEq,
    title: 'The handoff includes the details.',
    body: 'Upload project folders or stem packs. Browse files and preview supported audio inside the project before downloading what you need.',
    note: 'Listen to the render. Inspect the files. Give the next person the context to pick up where you left off.',
  },
  {
    label: 'Plugin discovery',
    icon: ExtensionOutlined,
    title: 'See what went into the sound.',
    body: 'Inspect detected plugins for a project version, grouped by format. Useful context before you open a collaborator’s session.',
    note: 'Experimental: detection can be incomplete or inaccurate. Plugins and licenses are not included.',
  },
];

export function LandingExplore() {
  const t = useModeTokens();
  const [view, setView] = useState(0);
  const selected = VIEWS[view];
  return (
    <Box
      component="section"
      id="lp-explore"
      sx={{
        ...ANCHOR,
        px: { xs: '20px', md: '40px' },
        py: { xs: '40px', md: '68px' },
      }}
    >
      <Eyebrow mb="16px">MORE THAN A PLACE TO PUT FILES</Eyebrow>
      <Headline min={32} max={54} sx={{ maxWidth: '22ch', mb: 3 }}>
        The whole session.
        <br />
        <span className="lp-gradient-text">Finally in context.</span>
      </Headline>
      <Tabs
        value={view}
        onChange={(_, next: number) => setView(next)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Explore StudioVault features"
        sx={{
          mb: 3,
          borderBottom: '1px solid',
          borderColor: t.borders.subtle,
          '& .MuiTab-root': {
            textTransform: 'none',
            minHeight: 58,
            px: { xs: 1.5, sm: 3 },
            fontSize: 13,
          },
        }}
      >
        {VIEWS.map(({ label, icon: Icon }, index) => (
          <Tab
            key={label}
            icon={<Icon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={label}
            id={`lp-tour-tab-${index}`}
            aria-controls={`lp-tour-panel-${index}`}
          />
        ))}
      </Tabs>
      <Box
        role="tabpanel"
        id={`lp-tour-panel-${view}`}
        aria-labelledby={`lp-tour-tab-${view}`}
        tabIndex={0}
      >
        <LandingSpotlight>
          <Box
            sx={{
              p: { xs: '22px', md: '38px' },
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0,1fr)',
                md: 'minmax(0,.8fr) minmax(0,1.2fr)',
              },
              gap: { xs: 3, md: 6 },
              alignItems: 'center',
            }}
          >
            <Box>
              <Mono size={10} color={t.colors.accentText}>
                0{view + 1} / {selected.label.toUpperCase()}
              </Mono>
              <Headline min={26} max={34} sx={{ mt: 2, mb: 2 }}>
                {selected.title}
              </Headline>
              <Box
                component="p"
                sx={{ color: t.text.secondary, lineHeight: 1.7, fontSize: 14 }}
              >
                {selected.body}
              </Box>
              <Box
                component="p"
                sx={{
                  color: t.text.muted,
                  lineHeight: 1.6,
                  fontSize: 12,
                  mt: 3,
                }}
              >
                {selected.note}
              </Box>
            </Box>
            <Box
              className="lp-gloss-inset"
              sx={{
                p: { xs: '16px', md: '24px' },
                border: '1px solid',
                borderColor: t.borders.medium,
                borderRadius: '12px',
                background: t.surfaces.trackSoft,
                minWidth: 0,
              }}
            >
              <Mono
                size={9}
                sx={{ display: 'block', mb: 2 }}
                letterSpacing="0.12em"
              >
                ILLUSTRATIVE{' '}
                {view === 0
                  ? 'LIBRARY'
                  : view === 1
                    ? 'FILE BROWSER'
                    : 'PLUGIN REPORT'}
              </Mono>
              {view === 0 && (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      gap: 2,
                    }}
                  >
                    {[
                      'Nightdrive',
                      'Coastline — stems',
                      'Room Tone 04',
                      'Paperweight',
                    ].map((name, i) => (
                      <Box key={name} sx={{ display: 'contents' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'center',
                            minWidth: 0,
                          }}
                        >
                          <FolderOutlined
                            sx={{ color: t.colors.accentText, fontSize: 22 }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Box
                              sx={{
                                fontWeight: 600,
                                fontSize: 13,
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {name}
                            </Box>
                            <Mono size={9}>{[14, 3, 2, 18][i]} versions</Mono>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            color: t.text.muted,
                            fontSize: 11,
                            display: 'flex',
                            gap: 0.5,
                            alignItems: 'center',
                          }}
                        >
                          {i === 1 ? (
                            'Stems'
                          ) : (
                            <>
                              <Launch sx={{ fontSize: 13 }} />
                              Open
                            </>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
              {view === 1 && (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  {[
                    'Nightdrive.als',
                    'Renders / rough-master.wav',
                    'Samples / vocal-comp.wav',
                  ].map((name, i) => (
                    <Box
                      key={name}
                      sx={{
                        pb: 1.5,
                        borderBottom: '1px solid',
                        borderColor: t.borders.subtle,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 1,
                          fontSize: 12,
                        }}
                      >
                        <Box sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                          {name}
                        </Box>
                        <Mono size={9}>{i === 0 ? 'ALS' : 'WAV'}</Mono>
                      </Box>
                      {i > 0 && (
                        <Waveform
                          bars={heroWave
                            .slice(i * 10, i * 10 + 32)
                            .map((bar) => ({
                              h: Math.max(2, Math.round(bar.h / 4)),
                              c: t.colors.accentFill,
                            }))}
                          height={30}
                        />
                      )}
                    </Box>
                  ))}
                  <Mono size={9}>
                    FILE PREVIEW EXAMPLE · NO AUDIO PLAYS IN THIS TOUR
                  </Mono>
                </Box>
              )}
              {view === 2 && (
                <>
                  <Box sx={{ color: t.colors.accentText, fontSize: 12, mb: 2 }}>
                    Plugin detection · Alpha
                  </Box>
                  {[
                    ['VST3', 'Plugin names', 'Detected in the session'],
                    [
                      'AU',
                      'Vendor and format',
                      'Inspect detected dependencies',
                    ],
                    [
                      'VST2',
                      'Detection details',
                      'Confidence and detection count',
                    ],
                  ].map(([format, type, detail]) => (
                    <Box
                      key={format}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        py: 1.5,
                        borderTop: '1px solid',
                        borderColor: t.borders.subtle,
                      }}
                    >
                      <ExtensionOutlined
                        sx={{ color: t.colors.cyan, fontSize: 21 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ fontSize: 12, fontWeight: 600 }}>{type}</Box>
                        <Box
                          sx={{ color: t.text.muted, fontSize: 11, mt: 0.5 }}
                        >
                          {detail}
                        </Box>
                      </Box>
                      <Mono size={9}>{format}</Mono>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Box>
        </LandingSpotlight>
      </Box>
    </Box>
  );
}
